// ============================================================================
// Unified Toast Notification Manager
// Consolidates comment, resource, and field-update notification systems into
// a single, variant-driven manager.
// ============================================================================

import { escapeHtml } from 'shared/utils/dom-helpers.js';
import { truncateString } from 'shared/utils/strings.js';

// ============================================================================
// Variant Configuration
// ============================================================================

const VARIANT_CONFIG = {
    // --- Simple status toasts (replace old showToast) ---
    success: {
        borderColor: 'var(--color-success)',
        accentColor: 'var(--color-success)',
        defaultDismiss: 3000,
        defaultSound: false,
        defaultIcon: 'fa-check-circle',
        headerTitle: null,  // simple toasts have no header
        simple: true
    },
    error: {
        borderColor: 'var(--color-danger)',
        accentColor: 'var(--color-danger)',
        defaultDismiss: 4000,
        defaultSound: false,
        defaultIcon: 'fa-exclamation-circle',
        headerTitle: null,
        simple: true
    },
    warning: {
        borderColor: 'var(--color-warning)',
        accentColor: 'var(--color-warning)',
        defaultDismiss: 3500,
        defaultSound: false,
        defaultIcon: 'fa-exclamation-triangle',
        headerTitle: null,
        simple: true
    },
    info: {
        borderColor: 'var(--color-primary)',
        accentColor: 'var(--color-primary)',
        defaultDismiss: 3000,
        defaultSound: false,
        defaultIcon: 'fa-info-circle',
        headerTitle: null,
        simple: true
    },
    // --- Rich generic variant (for leave, budget, client, service notifications) ---
    notification: {
        headerGradient: 'var(--color-primary) 0%, var(--color-info) 100%',
        borderColor: 'var(--color-primary)',
        progressGradient: 'var(--color-primary) 0%, var(--color-info) 100%',
        accentColor: 'var(--color-primary)',
        defaultDismiss: 15000,
        defaultSound: false,
        defaultIcon: 'fa-bell',
        headerTitle: 'Notification'
    },
    // --- Rich notification variants ---
    comment: {
        headerGradient: 'var(--color-info) 0%, var(--color-cyan) 100%',
        borderColor: 'var(--color-info)',
        progressGradient: 'var(--color-info) 0%, var(--color-cyan) 100%',
        accentColor: 'var(--color-info)',
        defaultDismiss: 15000,
        defaultSound: true,
        defaultIcon: 'fa-comment',
        headerTitle: 'New Comment'
    },
    assigned: {
        headerGradient: 'var(--color-success) 0%, var(--color-teal) 100%',
        borderColor: 'var(--color-success)',
        progressGradient: 'var(--color-success) 0%, var(--color-teal) 100%',
        accentColor: 'var(--color-success)',
        defaultDismiss: 15000,
        defaultSound: true,
        defaultIcon: 'fa-user-plus',
        headerTitle: 'Task Assigned'
    },
    unassigned: {
        headerGradient: 'var(--color-amber) 0%, var(--color-warning) 100%',
        borderColor: 'var(--color-amber)',
        progressGradient: 'var(--color-amber) 0%, var(--color-warning) 100%',
        accentColor: 'var(--color-amber)',
        defaultDismiss: 15000,
        defaultSound: true,
        defaultIcon: 'fa-user-minus',
        headerTitle: 'Task Reassigned'
    },
    reaction: {
        headerGradient: 'var(--color-pink) 0%, var(--color-info) 100%',
        borderColor: 'var(--color-pink)',
        progressGradient: 'var(--color-pink) 0%, var(--color-info) 100%',
        accentColor: 'var(--color-pink)',
        defaultDismiss: 15000,
        defaultSound: true,
        defaultIcon: 'fa-heart',
        headerTitle: 'Reaction'
    },
    'resource-claim': {
        headerGradient: 'var(--color-success) 0%, var(--color-teal) 100%',
        borderColor: 'var(--color-success)',
        progressGradient: 'var(--color-success) 0%, var(--color-teal) 100%',
        accentColor: 'var(--color-teal)',
        defaultDismiss: 4000,
        defaultSound: false,
        defaultIcon: 'fa-plug',
        headerTitle: 'Resource Connected'
    },
    'resource-release': {
        headerGradient: 'var(--color-text-secondary) 0%, var(--color-text-muted) 100%',
        borderColor: 'var(--color-text-muted)',
        progressGradient: 'var(--color-text-secondary) 0%, var(--color-text-muted) 100%',
        accentColor: 'var(--color-text-secondary)',
        defaultDismiss: 4000,
        defaultSound: false,
        defaultIcon: 'fa-plug',
        headerTitle: 'Resource Released'
    },
    'field-update': {
        headerGradient: 'var(--color-primary) 0%, var(--color-info) 100%',
        borderColor: 'var(--color-primary)',
        progressGradient: 'var(--color-primary) 0%, var(--color-info) 100%',
        accentColor: 'var(--color-primary)',
        defaultDismiss: 30000,
        defaultSound: false,
        defaultIcon: null, // uses SVG
        headerTitle: 'Live Update'
    },
    'task-created': {
        headerGradient: 'var(--color-success) 0%, var(--color-teal) 100%',
        borderColor: 'var(--color-success)',
        progressGradient: 'var(--color-success) 0%, var(--color-teal) 100%',
        accentColor: 'var(--color-success)',
        defaultDismiss: 15000,
        defaultSound: false,
        defaultIcon: null, // uses SVG
        headerTitle: 'Task Created'
    },
    'title-unlocked': {
        headerGradient: 'var(--color-warning) 0%, var(--color-amber) 100%',
        borderColor: 'var(--color-warning)',
        progressGradient: 'var(--color-warning) 0%, var(--color-amber) 100%',
        accentColor: 'var(--color-warning)',
        defaultDismiss: 10000,
        defaultSound: true,
        defaultIcon: null, // uses SVG trophy
        headerTitle: 'Title Unlocked!'
    }
};

// SVG icons for variants that don't use Font Awesome
const SVG_ICONS = {
    comment: `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
    assigned: `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>`,
    unassigned: `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/></svg>`,
    reaction: `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`,
    checkmark: `<svg viewBox="0 0 24 24" width="20" height="20"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/></svg>`,
    'title-unlocked': `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9V2h12v7"/><path d="M6 9a6 6 0 0 0 12 0"/><path d="M12 15v4"/><path d="M8 19h8"/><path d="M2 2l4 7"/><path d="M22 2l-4 7"/></svg>`
};

const CLOSE_SVG = `<svg viewBox="0 0 24 24" width="14" height="14"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/></svg>`;

// ============================================================================
// Toast Manager
// ============================================================================

class ToastManager {
    constructor() {
        this.container = null;
        this.notifications = new Map();
        this.audioContext = null;
    }

    /**
     * Create the container element on body (idempotent)
     */
    init() {
        if (this.container && this.container.parentNode) return;

        const existing = document.getElementById('toast-container');
        if (existing) {
            this.container = existing;
            return;
        }

        this.container = document.createElement('div');
        this.container.id = 'toast-container';
        this.container.className = 'toast-container';
        document.body.appendChild(this.container);
    }

    // ========================================================================
    // Preferences (localStorage - same key as old commentNotificationManager)
    // ========================================================================

    getPreferences() {
        const defaultPrefs = { enabled: true, sound: true, showPreview: true };
        try {
            const stored = localStorage.getItem('commentNotificationPrefs');
            if (stored) return { ...defaultPrefs, ...JSON.parse(stored) };
        } catch (e) {
            console.warn('[ToastManager] Failed to parse preferences:', e);
        }
        return defaultPrefs;
    }

    savePreferences(prefs) {
        try {
            const current = this.getPreferences();
            localStorage.setItem('commentNotificationPrefs', JSON.stringify({ ...current, ...prefs }));
        } catch (e) {
            console.warn('[ToastManager] Failed to save preferences:', e);
        }
    }

    // ========================================================================
    // Sound
    // ========================================================================

    playNotificationSound() {
        if (!document.hasFocus()) return;
        const prefs = this.getPreferences();
        if (!prefs.sound) return;

        try {
            if (!this.audioContext) {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }
            const ctx = this.audioContext;
            const now = ctx.currentTime;

            // First tone - A5
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            osc1.frequency.setValueAtTime(880, now);
            osc1.type = 'sine';
            gain1.gain.setValueAtTime(0.3, now);
            gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
            osc1.start(now);
            osc1.stop(now + 0.3);

            // Second tone - E6
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.frequency.setValueAtTime(1320, now + 0.1);
            osc2.type = 'sine';
            gain2.gain.setValueAtTime(0, now);
            gain2.gain.setValueAtTime(0.2, now + 0.1);
            gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
            osc2.start(now + 0.1);
            osc2.stop(now + 0.5);
        } catch (e) {
            console.warn('[ToastManager] Could not play sound:', e);
        }
    }

    // ========================================================================
    // Show
    // ========================================================================

    /**
     * Show a toast notification.
     *
     * @param {Object} options
     * @param {string} options.variant - One of the VARIANT_CONFIG keys
     * @param {string} [options.title] - Header title (falls back to variant default)
     * @param {string} [options.message] - Primary body text (plain text, will be escaped)
     * @param {string} [options.bodyHtml] - Raw HTML body content (bypasses message/subtitle)
     * @param {string} [options.subtitle] - Secondary text below message
     * @param {boolean} [options.subtitleHtml] - If true, subtitle contains HTML (not escaped)
     * @param {string} [options.avatarInitials] - Name to derive initials for avatar circle
     * @param {string} [options.iconClass] - Font Awesome icon class override
     * @param {string} [options.iconSvg] - SVG icon HTML override
     * @param {number} [options.dismissAfter] - Auto-dismiss ms (overrides variant default)
     * @param {boolean} [options.playSound] - Play sound (overrides variant default)
     * @param {Function} [options.onClick] - Click handler for body area
     * @param {string} [options.actionLabel] - Action button label (e.g. "Undo", "Restart"). Requires onAction.
     * @param {Function} [options.onAction] - Async handler invoked when the action button is clicked. The toast dismisses on resolve; on reject the button re-enables.
     * @param {string} [options.actionHint] - Footer hint text (e.g. "Click to view task")
     * @param {string} [options.footerLeft] - Footer left text (e.g. client name)
     * @param {boolean} [options.showTime] - Show relative time in footer that auto-updates
     * @returns {string} Notification ID
     */
    show(options) {
        if (!this.container) this.init();

        const variant = options.variant || 'comment';
        const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.comment;

        // Simple toasts always show; rich toasts respect prefs
        if (!config.simple) {
            const prefs = this.getPreferences();
            if (!prefs.enabled) return null;
        }

        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const dismissAfter = options.dismissAfter ?? config.defaultDismiss;
        const shouldSound = options.playSound ?? config.defaultSound;

        if (shouldSound) {
            this.playNotificationSound();
        }

        const el = document.createElement('div');
        el.id = id;
        el.setAttribute('role', 'alert');
        el.setAttribute('aria-live', 'polite');

        // Simple toasts use a compact layout (no header)
        if (config.simple) {
            el.className = `toast toast--simple toast--simple-enter toast--${variant}`;
            const iconCls = options.iconClass || config.defaultIcon;
            const hasAction = !!(options.actionLabel && options.onAction);
            el.innerHTML = `
                <div class="toast__simple-icon"><i class="fas ${iconCls}"></i></div>
                <span class="toast__simple-text">${escapeHtml(options.message || '')}</span>
                ${hasAction ? `<button type="button" class="toast__action-btn">${escapeHtml(options.actionLabel)}</button>` : ''}
            `;
            if (hasAction) {
                this._wireActionButton(el, id, options);
            }
        } else {
            el.className = `toast toast--enter toast--${variant}`;
            const title = options.title || config.headerTitle;
            const prefs = this.getPreferences();
            const iconHtml = this._buildIconHtml(options, config, variant);
            const bodyHtml = this._buildBodyHtml(options, prefs);
            const footerHtml = this._buildFooterHtml(options, config);

            el.innerHTML = `
                <div class="toast__header">
                    <div class="toast__icon">${iconHtml}</div>
                    <div class="toast__title">${escapeHtml(title)}</div>
                    <button class="toast__close" aria-label="Dismiss notification">${CLOSE_SVG}</button>
                </div>
                <div class="toast__body">${bodyHtml}</div>
                ${footerHtml}
                <div class="toast__progress" style="animation-duration: ${dismissAfter}ms"></div>
            `;

            el.querySelector('.toast__close').addEventListener('click', (e) => {
                e.stopPropagation();
                this.dismiss(id);
            });

            if (options.onClick) {
                const body = el.querySelector('.toast__body');
                body.style.cursor = 'pointer';
                body.addEventListener('click', () => {
                    this.dismiss(id);
                    options.onClick();
                });

                const footer = el.querySelector('.toast__footer');
                if (footer) {
                    footer.style.cursor = 'pointer';
                    footer.addEventListener('click', () => {
                        this.dismiss(id);
                        options.onClick();
                    });
                }
            }

            // Hover pauses BOTH the auto-dismiss timer and the progress bar, so a
            // notification stays put while it is being read. Leaving gives a short
            // grace period before it dismisses.
            el.addEventListener('mouseenter', () => {
                const progress = el.querySelector('.toast__progress');
                if (progress) progress.style.animationPlayState = 'paused';
                if (el._dismissTimeout) {
                    clearTimeout(el._dismissTimeout);
                    el._dismissTimeout = null;
                }
            });
            el.addEventListener('mouseleave', () => {
                if (el._dismissTimeout) return;
                const progress = el.querySelector('.toast__progress');
                if (progress) {
                    progress.style.animation = 'none';
                    void progress.offsetWidth; // reflow so the restart takes effect
                    progress.style.animation = 'toast-progress-shrink 5000ms linear forwards';
                }
                el._dismissTimeout = setTimeout(() => this.dismiss(id), 5000);
            });
        }

        // Remove existing simple toast if showing a new one
        if (config.simple) {
            this.notifications.forEach((existing, existingId) => {
                if (existing.classList.contains('toast--simple')) {
                    this.dismiss(existingId);
                }
            });
        }

        this.container.appendChild(el);
        this.notifications.set(id, el);

        // Confetti burst for title unlocks
        if (variant === 'title-unlocked') {
            this._spawnConfetti(el);
        }

        requestAnimationFrame(() => {
            if (config.simple) {
                el.classList.remove('toast--simple-enter');
            } else {
                el.classList.remove('toast--enter');
                el.classList.add('toast--active');
            }
        });

        const timeout = setTimeout(() => this.dismiss(id), dismissAfter);
        el._dismissTimeout = timeout;

        if (options.showTime) {
            this._startTimeUpdater(id, Date.now());
        }

        return id;
    }

    // ========================================================================
    // Internal builders
    // ========================================================================

    _buildIconHtml(options, config, variant) {
        // Explicit SVG override
        if (options.iconSvg) return options.iconSvg;

        // Explicit Font Awesome override
        if (options.iconClass) {
            return `<i class="fas ${escapeHtml(options.iconClass)}"></i>`;
        }

        // Variant-specific SVG icons (comment, assigned, unassigned, reaction, checkmark)
        if (SVG_ICONS[variant]) return SVG_ICONS[variant];

        // field-update and task-created use checkmark SVG
        if (!config.defaultIcon) return SVG_ICONS.checkmark;

        // Default: Font Awesome from config
        return `<i class="fas ${config.defaultIcon}"></i>`;
    }

    _buildBodyHtml(options, prefs) {
        // Raw HTML body - trust caller
        if (options.bodyHtml) return options.bodyHtml;

        let html = '';

        // Avatar row (for comments)
        if (options.avatarInitials) {
            const initials = this._getInitials(options.avatarInitials);
            html += `
                <div class="toast__commenter">
                    <div class="toast__avatar">${initials}</div>
                    <span class="toast__commenter-name">${escapeHtml(options.avatarInitials)}</span>
                </div>
            `;
        }

        // Main message
        if (options.message) {
            html += `<div class="toast__message">${escapeHtml(options.message)}</div>`;
        }

        // Subtitle / preview
        if (options.subtitle) {
            if (options.subtitleHtml) {
                html += `<div class="toast__subtitle">${options.subtitle}</div>`;
            } else if (prefs.showPreview !== false) {
                html += `<div class="toast__subtitle">${escapeHtml(truncateString(options.subtitle, 100))}</div>`;
            }
        }

        return html;
    }

    /**
     * Wire an action button inside a toast. On click runs options.onAction; on
     * resolve the toast is dismissed, on reject the button is re-enabled so
     * the user can retry.
     */
    _wireActionButton(el, id, options) {
        const btn = el.querySelector('.toast__action-btn');
        if (!btn) return;
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            // Cancel auto-dismiss while the action runs.
            if (el._dismissTimeout) {
                clearTimeout(el._dismissTimeout);
                el._dismissTimeout = null;
            }
            const originalLabel = btn.textContent;
            btn.disabled = true;
            btn.textContent = 'Working...';
            try {
                await options.onAction();
                this.dismiss(id);
            } catch (err) {
                console.error('[ToastManager] action handler failed:', err);
                btn.disabled = false;
                btn.textContent = originalLabel;
            }
        });
    }

    _buildFooterHtml(options, config) {
        const hasHint = !!options.actionHint;
        const hasLeft = !!options.footerLeft;
        const hasTime = !!options.showTime;

        if (!hasHint && !hasLeft && !hasTime) return '';

        let inner = '';
        if (hasHint) {
            inner = `<span class="toast__action-hint">${escapeHtml(options.actionHint)}</span>`;
        } else if (hasLeft || hasTime) {
            inner = `
                ${hasLeft ? `<span class="toast__footer-label">${escapeHtml(options.footerLeft)}</span>` : '<span></span>'}
                ${hasTime ? `<span class="toast__time">Just now</span>` : ''}
            `;
        }

        return `<div class="toast__footer">${inner}</div>`;
    }

    // ========================================================================
    // Dismiss
    // ========================================================================

    dismiss(id) {
        const el = this.notifications.get(id);
        if (!el) return;

        if (el._dismissTimeout) clearTimeout(el._dismissTimeout);

        const isSimple = el.classList.contains('toast--simple');
        if (isSimple) {
            el.classList.add('toast--simple-exit');
        } else {
            el.classList.remove('toast--active');
            el.classList.add('toast--exit');
        }

        setTimeout(() => {
            if (el.parentNode) el.parentNode.removeChild(el);
            this.notifications.delete(id);
        }, 300);
    }

    dismissAll() {
        this.notifications.forEach((_, id) => this.dismiss(id));
    }

    // ========================================================================
    // Helpers
    // ========================================================================

    _getInitials(name) {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }

    _startTimeUpdater(id, startTime) {
        const update = () => {
            const el = this.notifications.get(id);
            if (!el) return;

            const timeEl = el.querySelector('.toast__time');
            if (!timeEl) return;

            const seconds = Math.floor((Date.now() - startTime) / 1000);
            if (seconds < 5) timeEl.textContent = 'Just now';
            else if (seconds < 60) timeEl.textContent = `${seconds} seconds ago`;
            else if (seconds < 120) timeEl.textContent = '1 minute ago';
            else timeEl.textContent = `${Math.floor(seconds / 60)} minutes ago`;

            if (this.notifications.has(id)) {
                setTimeout(update, 5000);
            }
        };
        setTimeout(update, 5000);
    }

    /**
     * Spawn CSS confetti particles above a toast element
     */
    _spawnConfetti(toastEl) {
        const confetti = document.createElement('div');
        confetti.className = 'toast__confetti';
        // 14 particles with staggered delays and varied positions
        for (let i = 0; i < 14; i++) {
            const particle = document.createElement('span');
            particle.className = 'toast__confetti-particle';
            particle.style.left = `${6 + (i / 13) * 88}%`;
            particle.style.animationDelay = `${i * 0.06}s`;
            confetti.appendChild(particle);
        }
        toastEl.style.overflow = 'visible';
        toastEl.prepend(confetti);
        // Remove after animation completes
        setTimeout(() => confetti.remove(), 2000);
    }

    // ========================================================================
    // Cleanup
    // ========================================================================

    destroy() {
        this.dismissAll();
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        if (this.container?.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
        this.container = null;
    }
}

// Singleton
export const toastManager = new ToastManager();

// ============================================================================
// Bridge: showToast() - Drop-in replacement for old shared/toast/toast.js
// ============================================================================

/**
 * Show a simple status toast notification.
 *
 * Two call forms:
 *   showToast('Saved', 'success')
 *   showToast({ message, type, duration, actionLabel, onAction })
 *
 * The object form supports an action button (e.g. Undo) via actionLabel + onAction.
 * onAction is awaited; on resolve the toast is dismissed, on reject the button is re-enabled.
 *
 * @param {string|Object} messageOrOptions - Text to display, or an options object.
 * @param {'success'|'error'|'warning'|'info'} [type='success'] - Toast type (ignored when passing an options object).
 * @param {number} [duration] - Auto-dismiss in ms (ignored when passing an options object).
 * @returns {HTMLElement|null} The toast element.
 */
export function showToast(messageOrOptions, type = 'success', duration) {
    let opts;
    if (typeof messageOrOptions === 'string') {
        opts = { variant: type, message: messageOrOptions };
        if (duration != null) opts.dismissAfter = duration;
    } else if (messageOrOptions && typeof messageOrOptions === 'object') {
        const { type: optType, duration: optDuration, ...rest } = messageOrOptions;
        opts = { variant: optType || 'success', ...rest };
        if (optDuration != null) opts.dismissAfter = optDuration;
    } else {
        return null;
    }
    const id = toastManager.show(opts);
    return id ? toastManager.notifications.get(id) : null;
}
